import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowUpRight, ChevronDown, Download, Share2 } from 'lucide-react';
import { nodeIcon } from '@/components/value-chain/nodeVisuals';
import { ProductRevenueMapView } from '@/components/value-chain/explorer/ProductRevenueMapView';
import { StarlinkConsumerView } from '@/components/value-chain/explorer/StarlinkConsumerView';
import { StarlinkProductView } from '@/components/value-chain/explorer/StarlinkProductView';
import { resolveProductRevenue, type ProductLine } from '@/components/value-chain/productRevenue';
import { isStarlinkConsumerId } from '@/components/value-chain/starlinkConsumer';
import { isStarlinkLineId } from '@/components/value-chain/starlinkProduct';
import {
  DEFAULT_REVENUE_FILTERS,
  buildRevenueMap,
  collectRevenueSlices,
  displayCompanyName,
  formatGrowth,
  formatUsdCompact,
  isSyntheticCompany,
  knownBrand,
  type RevenueCategory,
  type RevenueCompany,
  type RevenueMapFilters,
} from '@/components/value-chain/revenueMap';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import type { ValueChainGraph, ValueChainNode } from '@/types/valueChain';

type Layer = 'products' | 'line' | 'consumer' | 'suppliers';

type Props = {
  graph: ValueChainGraph;
  graphs: ValueChainGraph[];
  index: TreeIndex;
  root: ValueChainNode;
  onSelect: (id: string) => void;
  onViewDetails: (id: string) => void;
  onOpenGraph?: (id: string) => void;
  onFocusChange?: (label: string | null) => void;
  resetNonce?: number;
};

const BRAND_COLOR: Record<string, string> = {
  st: '#1f7a3a',
  ti: '#c62828',
  nxp: '#111827',
  infineon: '#0f766e',
  qorvo: '#c45c26',
  adi: '#1d4ed8',
  onsemi: '#ea580c',
  qualcomm: '#14532d',
  mediatek: '#1d4ed8',
  broadcom: '#be123c',
  murata: '#dc2626',
  tdk: '#111827',
  molex: '#111827',
  amphenol: '#1d4ed8',
  te: '#d97706',
  flex: '#0284c7',
  jabil: '#1e293b',
  chicony: '#0f766e',
  neweb: '#1d4ed8',
  '3m': '#dc2626',
  microchip: '#c2410c',
  delta: '#166534',
  foxconn: '#1e3a8a',
};

export function RevenueMapView({
  graph,
  graphs,
  index,
  root,
  onSelect,
  onViewDetails,
  onOpenGraph,
  onFocusChange,
  resetNonce = 0,
}: Props) {
  const [layer, setLayer] = useState<Layer>('products');
  const [focusLine, setFocusLine] = useState<ProductLine | null>(null);
  const [filters, setFilters] = useState<RevenueMapFilters>(DEFAULT_REVENUE_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const skipReset = useRef(true);

  const productModel = useMemo(
    () => resolveProductRevenue(graph, graphs, index),
    [graph, graphs, index],
  );

  useEffect(() => {
    if (skipReset.current) {
      skipReset.current = false;
      return;
    }
    setLayer('products');
    setFocusLine(null);
  }, [resetNonce]);
  const slices = useMemo(() => collectRevenueSlices(graph, index), [graph, index]);
  const model = useMemo(
    () => buildRevenueMap(graph, index, slices, filters),
    [filters, graph, index, slices],
  );

  if (layer === 'products') {
    return (
      <ProductRevenueMapView
        model={productModel}
        graphs={graphs}
        onOpenLine={(line, matched) => {
          setFocusLine(line);
          if (matched && matched.id !== graph.id) onOpenGraph?.(matched.id);
          if (isStarlinkConsumerId(line.id)) {
            setLayer('consumer');
            onFocusChange?.('Consumer');
            return;
          }
          if (isStarlinkLineId(line.id)) {
            setLayer('line');
            onFocusChange?.('Starlink');
            return;
          }
          setLayer('suppliers');
          onFocusChange?.(line.label);
        }}
      />
    );
  }

  if (layer === 'line') {
    return (
      <StarlinkProductView
        onBack={() => {
          setLayer('products');
          setFocusLine(null);
          onFocusChange?.(null);
        }}
        onOpenValueChain={() => {
          onViewDetails(root.id);
        }}
        onOpenSuppliers={() => setLayer('suppliers')}
        onOpenConsumer={() => {
          setLayer('consumer');
          onFocusChange?.('Consumer');
        }}
      />
    );
  }

  if (layer === 'consumer') {
    return (
      <StarlinkConsumerView
        onBackToMap={() => {
          setLayer('products');
          setFocusLine(null);
          onFocusChange?.(null);
        }}
        onBackToStarlink={() => {
          setLayer('line');
          onFocusChange?.('Starlink');
        }}
        onOpenValueChain={() => {
          onViewDetails(root.id);
        }}
        onOpenSuppliers={() => setLayer('suppliers')}
      />
    );
  }

  const selected =
    model.companies.find((company) => company.id === selectedId) ?? model.companies[0] ?? null;
  const barTotal = model.categories.reduce((sum, category) => sum + category.revenue, 0) || 1;
  const rows = chunk(model.categories, 3);
  const RootIcon = nodeIcon(root);

  const handleSelect = (company: RevenueCompany) => {
    if (isSyntheticCompany(company.id)) return;
    setSelectedId(company.id);
    if (company.node) onSelect(company.node.id);
  };

  const handleExport = () => {
    const csv = [
      ['category', 'company', 'revenue_usd', 'growth_pct', 'country'],
      ...model.categories.flatMap((category) =>
        category.companies.map((company) => [
          category.label,
          displayCompanyName(company.name),
          String(Math.round(company.revenue)),
          company.growthPct == null ? '' : String(Math.round(company.growthPct)),
          company.country,
        ]),
      ),
    ]
      .map((row) => row.map(csvCell).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${graph.product_slug || graph.id}-revenue-map.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    const lines = [
      `${graph.name} — Supplier Revenue Exposure`,
      `Estimated supplier revenue: ${formatUsdCompact(model.totalRevenue)} / year`,
      ...model.companies
        .slice(0, 8)
        .map((company) => `${displayCompanyName(company.name)}: ${formatUsdCompact(company.revenue)}`),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f7f8fa] px-6 py-4">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => {
              if (focusLine && isStarlinkConsumerId(focusLine.id)) {
                setLayer('consumer');
                onFocusChange?.('Consumer');
                return;
              }
              if (focusLine && isStarlinkLineId(focusLine.id)) {
                setLayer('line');
                onFocusChange?.('Starlink');
                return;
              }
              setLayer('products');
              setFocusLine(null);
              onFocusChange?.(null);
            }}
            className="mb-1 inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {focusLine && isStarlinkLineId(focusLine.id) ? 'Starlink' : `${productModel.company} Revenue Map`}
          </button>
          <h2 className="text-[22px] leading-tight font-semibold tracking-tight text-slate-900">
            {focusLine?.label ?? graph.name} – Supplier Revenue Exposure
          </h2>
          <p className="mt-0.5 text-[13px] text-slate-500">
            Estimated annual supplier revenue exposure by company (USD)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect value="estimated" options={[{ id: 'estimated', label: 'Estimated Revenue' }]} onChange={() => undefined} />
          <FilterSelect
            value={filters.tier}
            options={[
              { id: 'all', label: 'All Tiers' },
              { id: 'critical', label: 'Critical (rank 1–2)' },
              { id: 'high', label: 'High (rank 3–4)' },
              { id: 'other', label: 'Other (rank 5+)' },
            ]}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, tier: value as RevenueMapFilters['tier'] }))
            }
          />
          <FilterSelect
            value={filters.categoryId}
            options={[
              { id: 'all', label: 'All Components' },
              ...model.categoryOptions.map((item) => ({ id: item.id, label: item.label })),
            ]}
            onChange={(value) => setFilters((prev) => ({ ...prev, categoryId: value }))}
          />
          <FilterSelect
            value={filters.region}
            options={[
              { id: 'all', label: 'All Regions' },
              ...model.regions.map((region) => ({ id: region, label: region })),
            ]}
            onChange={(value) => setFilters((prev) => ({ ...prev, region: value }))}
          />
          <span className="hidden w-2 sm:block" />
          <FilterSelect
            value={filters.sizeBy}
            options={[
              { id: 'revenue', label: 'Size: Revenue' },
              { id: 'mentions', label: 'Size: Mentions' },
            ]}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, sizeBy: value as RevenueMapFilters['sizeBy'] }))
            }
          />
          <FilterSelect
            value={filters.colorBy}
            options={[
              { id: 'growth', label: 'Color: Growth' },
              { id: 'evidence', label: 'Color: Evidence' },
              { id: 'share', label: 'Color: Share' },
            ]}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, colorBy: value as RevenueMapFilters['colorBy'] }))
            }
          />
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
        <div className="flex min-h-0 min-w-0 flex-col gap-2">
          {rows.length === 0 ? (
            <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white text-sm text-slate-500">
              No mapped supplier spend for these filters.
            </div>
          ) : (
            rows.map((row) => (
              <div key={row.map((category) => category.id).join('-')} className="flex min-h-0 flex-1 gap-2">
                {row.map((category) => (
                  <div
                    key={category.id}
                    className="min-h-0 min-w-0"
                    style={{
                      flex: row.length === 1 ? '0 1 34%' : Math.max(category.revenue, 1),
                    }}
                  >
                    <CategoryCard
                      category={category}
                      colorBy={filters.colorBy}
                      selectedId={selected?.id ?? null}
                      onSelect={handleSelect}
                    />
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto">
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                <RootIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{graph.name}</p>
                <p className="text-[11px] text-slate-400">Total estimated supplier revenue</p>
              </div>
            </div>
            <p className="mt-3 text-[28px] leading-none font-semibold tracking-tight text-slate-900">
              {formatUsdCompact(model.totalRevenue)}{' '}
              <span className="text-lg font-medium text-slate-400">/ year</span>
            </p>
            <p className="mt-1.5 text-[12px] text-slate-500">
              ~{model.supplierCount.toLocaleString()} suppliers, {model.nodeCount.toLocaleString()} nodes
            </p>
            <div className="mt-4 space-y-2">
              {model.categories.map((category) => (
                <div key={category.id} className="flex items-center gap-2 text-[12px]">
                  <span className="w-[118px] truncate text-slate-600">{category.label}</span>
                  <span className="h-2 flex-1 rounded-full bg-slate-100">
                    <span
                      className="block h-2 rounded-full bg-emerald-500"
                      style={{ width: `${Math.max(4, (category.revenue / barTotal) * 100)}%` }}
                    />
                  </span>
                  <span className="w-8 text-right tabular-nums text-slate-500">
                    {Math.round((category.revenue / barTotal) * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Key Insights</h3>
            <ol className="mt-3 space-y-2.5">
              {model.insights.map((insight, i) => (
                <li key={insight} className="flex gap-2.5 text-[12px] leading-relaxed text-slate-600">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-medium text-white">
                    {i + 1}
                  </span>
                  <span>{insight}</span>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => onViewDetails(root.id)}
              className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-blue-700 hover:text-blue-900"
            >
              View full analysis
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Selected Company</h3>
            {selected ? (
              <>
                <div className="mt-3 flex items-center gap-3">
                  <BrandMark name={selected.name} large />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {displayCompanyName(selected.name)}
                    </p>
                    <p className="text-[11px] text-slate-400">{selected.country}</p>
                  </div>
                </div>
                <dl className="mt-3 space-y-1.5 text-[12px]">
                  <Row label="Estimated revenue" value={`${formatUsdCompact(selected.revenue)} / year`} />
                  <Row
                    label="YoY change"
                    value={formatGrowth(selected.growthPct)}
                    valueClass={
                      selected.growthPct != null && selected.growthPct < 0
                        ? 'text-rose-600'
                        : 'text-emerald-700'
                    }
                  />
                  <Row
                    label="Key components"
                    value={selected.partNames.slice(0, 4).join(', ') || 'Mapped parts'}
                  />
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Confidence</dt>
                    <dd>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          selected.evidence === 'confirmed'
                            ? 'bg-emerald-50 text-emerald-700'
                            : selected.evidence === 'inferred' || selected.evidence === 'mixed'
                              ? 'bg-amber-50 text-amber-800'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {selected.evidence === 'confirmed'
                          ? 'High'
                          : selected.evidence === 'inferred' || selected.evidence === 'mixed'
                            ? 'Medium'
                            : 'Low'}
                      </span>
                    </dd>
                  </div>
                </dl>
                {selected.node ? (
                  <button
                    type="button"
                    onClick={() => onViewDetails(selected.node!.id)}
                    className="mt-3 text-[12px] font-medium text-blue-700 hover:text-blue-900"
                  >
                    View company details
                  </button>
                ) : null}
              </>
            ) : (
              <p className="mt-3 text-[13px] text-slate-500">Select a company tile to inspect it.</p>
            )}
          </section>
        </aside>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          <LegendSwatch className="bg-[#157a3a]" label="> +20%" />
          <LegendSwatch className="bg-[#4ade80]" label="+10% to +20%" />
          <LegendSwatch className="bg-[#bbf7d0] ring-1 ring-emerald-200" label="0% to +10%" />
          <LegendSwatch className="bg-[#fecdd3]" label="−10% to 0%" />
          <LegendSwatch className="bg-[#f43f5e]" label="< −10%" />
          <LegendSwatch className="bg-gray-200" label="No estimate" />
        </div>
        <div className="flex items-center gap-3 text-[12px] text-slate-600">
          <span>
            Total mapped revenue (est.){' '}
            <span className="font-semibold text-slate-900">{formatUsdCompact(model.totalRevenue)}</span>
          </span>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-slate-500 hover:bg-white hover:text-slate-800"
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </button>
          <button
            type="button"
            onClick={() => void handleShare()}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-slate-500 hover:bg-white hover:text-slate-800"
          >
            <Share2 className="h-3.5 w-3.5" />
            {copied ? 'Copied' : 'Share'}
          </button>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Note: Revenue estimates are based on public data, industry sources, and internal modeling. Not financial
        advice.
      </p>
    </div>
  );
}

function CategoryCard({
  category,
  colorBy,
  selectedId,
  onSelect,
}: {
  category: RevenueCategory;
  colorBy: RevenueMapFilters['colorBy'];
  selectedId: string | null;
  onSelect: (company: RevenueCompany) => void;
}) {
  const companies = category.companies;
  const total = companies.reduce((sum, company) => sum + company.revenue, 0) || 1;
  const [lead, ...rest] = companies;
  const leadLayout = Boolean(lead && rest.length >= 2 && lead.revenue / total >= 0.28);

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl bg-[#0d4f3c]">
      <header className="flex h-9 shrink-0 items-center justify-between gap-2 px-3 text-white">
        <h3 className="min-w-0 truncate text-[13px] font-medium">{category.label}</h3>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums">
          {formatUsdCompact(category.revenue)}
        </span>
      </header>
      <div className="min-h-0 flex-1 p-1 pt-0">
        {leadLayout && lead ? (
          <div className="flex h-full min-h-0 gap-1">
            <CompanyTile
              company={lead}
              size="lead"
              colorBy={colorBy}
              categoryRevenue={total}
              selected={lead.id === selectedId}
              onSelect={onSelect}
              className="w-[48%] min-w-[132px]"
            />
            <div className="grid min-h-0 min-w-0 flex-1 grid-cols-2 grid-rows-2 gap-1">
              {rest.slice(0, 4).map((company) => (
                <CompanyTile
                  key={company.id}
                  company={company}
                  size="cell"
                  colorBy={colorBy}
                  categoryRevenue={total}
                  selected={company.id === selectedId}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className={`grid h-full min-h-0 gap-1 ${
              companies.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'
            }`}
          >
            {companies.slice(0, 4).map((company) => (
              <CompanyTile
                key={company.id}
                company={company}
                size={companies.length === 1 ? 'lead' : 'cell'}
                colorBy={colorBy}
                categoryRevenue={total}
                selected={company.id === selectedId}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CompanyTile({
  company,
  size,
  colorBy,
  categoryRevenue,
  selected,
  onSelect,
  className = '',
}: {
  company: RevenueCompany;
  size: 'lead' | 'cell';
  colorBy: RevenueMapFilters['colorBy'];
  categoryRevenue: number;
  selected: boolean;
  onSelect: (company: RevenueCompany) => void;
  className?: string;
}) {
  const share = company.revenue / Math.max(1, categoryRevenue);
  const tone = tileTone(company, colorBy, share);
  const lead = size === 'lead';

  return (
    <button
      type="button"
      title={`${displayCompanyName(company.name)} · ${formatUsdCompact(company.revenue)}`}
      onClick={() => onSelect(company)}
      className={`flex h-full min-h-0 min-w-0 flex-col justify-between overflow-hidden rounded-md p-2.5 text-left ${className} ${
        selected ? 'ring-2 ring-white ring-offset-1 ring-offset-[#0d4f3c]' : ''
      }`}
      style={{ background: tone.bg, color: tone.fg }}
    >
      <span className="min-w-0">
        <BrandMark name={company.name} large={lead} />
        <span
          className={`mt-1.5 block font-semibold ${
            lead ? 'text-[15px] leading-snug' : 'line-clamp-2 text-[12px] leading-tight'
          }`}
        >
          {displayCompanyName(company.name)}
        </span>
      </span>
      <span>
        <span className={`block font-semibold tabular-nums ${lead ? 'text-[22px] leading-none' : 'text-[15px] leading-none'}`}>
          {formatUsdCompact(company.revenue)}
        </span>
        <span className={`mt-0.5 block ${lead ? 'text-[13px]' : 'text-[11px]'} opacity-80`}>
          {formatGrowth(company.growthPct)}
        </span>
      </span>
    </button>
  );
}

function BrandMark({ name, large = false }: { name: string; large?: boolean }) {
  const brand = knownBrand(name);
  const size = large ? 'h-8 w-8 text-[11px]' : 'h-5 w-5 text-[8px]';
  const label = brand?.short ?? (name === 'Various' || name === 'Other' ? '·' : displayCompanyName(name).slice(0, 2).toUpperCase());
  const background = (brand && BRAND_COLOR[brand.id]) || (name === 'Various' || name === 'Other' ? '#94a3b8' : '#334155');
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-md font-bold text-white ${size}`}
      style={{ background }}
      aria-hidden
    >
      {label}
    </span>
  );
}

function tileTone(
  company: RevenueCompany,
  colorBy: RevenueMapFilters['colorBy'],
  share: number,
): { bg: string; fg: string } {
  if (company.growthPct == null && colorBy === 'growth') {
    return { bg: '#e5e7eb', fg: '#4b5563' };
  }
  if (colorBy === 'evidence') {
    if (company.evidence === 'confirmed') return { bg: '#15803d', fg: '#ffffff' };
    if (company.evidence === 'inferred' || company.evidence === 'mixed') return { bg: '#fde68a', fg: '#78350f' };
    return { bg: '#e5e7eb', fg: '#4b5563' };
  }
  if (colorBy === 'share') {
    if (share >= 0.35) return { bg: '#157a3a', fg: '#ffffff' };
    if (share >= 0.15) return { bg: '#4ade80', fg: '#14532d' };
    return { bg: '#bbf7d0', fg: '#14532d' };
  }
  const value = company.growthPct ?? 0;
  if (value > 20) return { bg: '#157a3a', fg: '#ffffff' };
  if (value >= 10) return { bg: '#4ade80', fg: '#14532d' };
  if (value >= 0) return { bg: '#bbf7d0', fg: '#14532d' };
  if (value > -10) return { bg: '#fecdd3', fg: '#9f1239' };
  return { bg: '#f43f5e', fg: '#ffffff' };
}

function FilterSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ id: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="relative">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="appearance-none rounded-lg border border-slate-200 bg-white py-1.5 pr-8 pl-3 text-[12px] text-slate-700 outline-none hover:border-slate-300"
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
    </label>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-sm ${className}`} />
      {label}
    </span>
  );
}

function Row({
  label,
  value,
  valueClass = 'font-medium text-slate-900',
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`max-w-[160px] text-right ${valueClass}`}>{value}</dd>
    </div>
  );
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}
